import { MonoBehavior } from 'game/core/monoBehavior';
import { addToLevel } from 'game/core/Playable2DRootContainer';
import { EventName } from 'game/events/eventsName';
import { GameEvent } from 'game/events/gameEvent';
import { Layout } from 'game/LayoutManager';
import { Container, Point } from 'pixi.js';
import gsap from 'gsap';
import { BUTTONS_BOTTOM_MARGIN_DESKTOP, DESKTOP_SIDEBAR_WIDTH, Displays } from 'constants/layout';
import { getScreenSize } from 'game/core/screen';
import { GamePlaceholderGraphicsAnimation } from './GamePlaceholderGraphicsAnimation';
import { GameName } from 'constants/difficulty';
import { Store } from 'game/store/gameStore';
import { getStrategyByGame, getStrategyViewByGame } from 'game/utils/strategy';

export class GamePlaceholder extends MonoBehavior {
    container: Container;
    placeholders: GamePlaceholderGraphicsAnimation[] = [];
    glowPosition = 0;

    constructor() {
        super();
        this.container = new Container();
        addToLevel(this.container);
        this.container.zIndex = 0;
        GameEvent.add(EventName.GAME_READY, this.destroy);
        GameEvent.add(EventName.BACKGROUND_LOADED, this.fade.bind(this));
    }
    fade() {
        gsap.to(this.container, { alpha: 0.3, duration: 0.2 });
    }
    drawPlaceholder(pos: Point) {
        const placeholder = new GamePlaceholderGraphicsAnimation();

        placeholder.draw((g) => {
            const size = Layout.getCardSize();
            g.roundRect(size.width / -2, size.height / -2, size.width, size.height, 10);
        });
        this.placeholders.push(placeholder);

        placeholder.graphic.position = pos;
        this.container.addChild(placeholder.graphic);

        return placeholder;
    }

    createPyramidPlaceholders() {
        const pyramidRows = 7;
        
        for (let row = 0; row < pyramidRows; row++) {
            for (let col = 0; col <= row; col++) {
                const pos = this.getPyramidCardPosition(row, col);
                this.drawPlaceholder(pos);
            }
        }
        
        for (let i = 0; i < Layout.foundations; i++) {
            this.drawPlaceholder(Layout.getFoundationPosition(i));
        }
        
        if (Layout.stockPile) {
            this.drawPlaceholder(Layout.getStockPilePosition());
        }
    }

    getPyramidCardPosition(row: number, col: number): Point {
        const cardSize = Layout.getCardSize();

        const startY = Layout.getPyramidTopPadding();

        const startX = 0;

        const x = startX + (col - row / 2) * (cardSize.width + (cardSize.width * 0.1)) + Layout.getPyramidXOffset();
        const y = startY + row * (cardSize.height * 0.4);
        return new Point(x, y);
    }

    createPlaceholders() {
        if (Store.name === GameName.PYRAMID) {
            const pyramidView = getStrategyViewByGame(Store.name);
            this.createPyramidPlaceholders();
            pyramidView?.destroy();
            return;
        }

        const { width, height } = getScreenSize();
            const display = Layout.getDisplay();
            for (let i = 0; i < Layout.columns; i++) {
                this.drawPlaceholder(Layout.getColumnPosition(i));
            }
            for (let i = 0; i < Layout.foundations; i++) {
                this.drawPlaceholder(Layout.getFoundationPosition(i));
            }

            for (let i = 0; i < Layout.freeCells; i++) {
                this.drawPlaceholder(Layout.getFreeCellPosition(i));
            }
            if (Layout.stockPile) {
                this.drawPlaceholder(Layout.getStockPilePosition());
            }
            if (display === Displays.DESKTOP) {
                const layoutRight = new GamePlaceholderGraphicsAnimation();
                layoutRight.draw((g) => {
                    g.rect(width / 2 - DESKTOP_SIDEBAR_WIDTH, -height / 2, DESKTOP_SIDEBAR_WIDTH, height);
                });

                this.container.addChild(layoutRight.graphic);
                this.placeholders.push(layoutRight);
            }
        
        this.createButtonPlaceholders();
    }
    
    createButtonPlaceholders() {
        const { width, height } = getScreenSize();
        const display = Layout.getDisplay();
        const buttonSize = Layout.getButtonSize();
        const padding = Layout.getButtonPadding();
        const startLeft = -width / 2 + buttonSize / 2 + padding.x;
        
        switch (display) {
            case Displays.MOBILE:
                for (let i = 0; i < 4; i++) {
                    if (i === 3) {
                        this.placeButton(startLeft, height / 2 - buttonSize / 2 - padding.y);
                    } else {
                        this.placeButton(
                            width / 2 - buttonSize / 2 + (0 - i) * (buttonSize + padding.x) - padding.x,
                            height / 2 - buttonSize / 2 - padding.y
                        );
                    }
                }
                break;
            case Displays.TABLET:
                for (let i = 0; i < 7; i++) {
                    let index = i;
                    if (i > 2) {
                        index = i - 3;
                        this.placeButton(
                            startLeft + index * buttonSize + index * padding.x * 1.5,
                            height / 2 - buttonSize / 2 - padding.y
                        );
                    } else {
                        this.placeButton(
                            width / 2 - buttonSize / 2 + (0 - index) * (buttonSize + padding.x) - padding.x,
                            height / 2 - buttonSize / 2 - padding.y
                        );
                    }
                }
                break;
            case Displays.TABLET:
                for (let i = 0; i < 7; i++) {
                    let index = i;
                    if (i > 2) {
                        index = i - 3;
                        this.placeButton(
                            startLeft + index * buttonSize + index * padding.x * 1.5,
                            height / 2 - buttonSize / 2 - padding.y
                        );
                    } else {
                        this.placeButton(
                            width / 2 - buttonSize / 2 + (0 - index) * (buttonSize + padding.x) - padding.x,
                            height / 2 - buttonSize / 2 - padding.y
                        );
                    }
                }
            case Displays.DESKTOP:
                for (let i = 0; i < 3; i++) {
                    this.placeButton(
                        -DESKTOP_SIDEBAR_WIDTH / 2 + (1 - i) * (buttonSize + padding.x),
                        height / 2 - buttonSize * 0.5 - BUTTONS_BOTTOM_MARGIN_DESKTOP
                    );
                }
                break;
        }
    }

    placeButton(x: number, y: number) {
        const buttonSize = Layout.getButtonSize();
        const button = new GamePlaceholderGraphicsAnimation();
        button.draw((g) => {
            g.circle(0, 0, buttonSize / 2);
        });

        button.graphic.position.set(x, y);

        this.container.addChild(button.graphic);
        this.placeholders.push(button);
    }
    resize(): void {
        this.clearPlaceholders();
        this.createPlaceholders();
    }
    clearPlaceholders(): void {
        while (this.placeholders.length) {
            const p = this.placeholders.pop();
            p?.destroy();
        }
        this.container.removeChildren();
    }
    destroy(): void {
        super.destroy();
        gsap.to(this.container, { alpha: 0, duration: 0.2, onComplete: this.clearPlaceholders.bind(this) });
    }
}
